/**
 * Le crochet de la relecture du quiz de prérequis : il branche la machine (`quizReview`) sur la
 * relecture du serveur (`GET activities/{id}/quiz`) et la télémétrie.
 *
 * Ce qui part d'ici :
 *  - la correction lue quand la feuille s'ouvre sans l'avoir (« Revoir le quiz », ou une tentative
 *    dont la réponse ne l'a pas portée) ; une fois lue, elle est gardée pour l'activité ;
 *  - un fil d'Ariane à chaque changement d'état, une erreur typée à chaque échec, aucun `catch` vide.
 *
 * L'état rendu suit l'activité passée au même rendu : la relecture d'une activité précédente ne se
 * montre jamais sur la suivante.
 */
import { useCallback, useEffect, useMemo } from "react"

import { type PhaseTransition, useSignalReducer, withPhaseTransitions } from "../../shared/useSignalReducer"
import { type EngineTelemetry } from "../../shared/telemetry"
import {
    initialQuizReviewState,
    quizReviewBlocks,
    quizReviewReducer,
    type ApiQuizForReview,
    type QuizReviewState,
} from "./quizReview"

export type UseQuizReviewOptions = {
    activityId: number | null
    /** `GET activities/{id}/quiz`. */
    getQuiz: (activityId: number) => Promise<ApiQuizForReview | null>
    telemetry: EngineTelemetry
}

export type QuizReviewHandle = {
    state: QuizReviewState
    /** La relecture ouverte à la fin du quiz retient l'activité. */
    blocking: boolean
    /** La tentative est faite : la feuille s'ouvre sur sa correction. */
    attempted: (quiz: ApiQuizForReview | null) => void
    /** « Revoir le quiz ». */
    open: () => void
    /** « Réessayer », après une relecture en échec. */
    retry: () => void
    close: () => void
}

const label = (state: QuizReviewState) =>
    state.open == null ? "review:closed" : `review:${state.open}:${state.status}`

const reduceWithTransitions = withPhaseTransitions(quizReviewReducer, label)

export function useQuizReview(options: UseQuizReviewOptions): QuizReviewHandle {
    const { activityId, getQuiz, telemetry } = options

    const [stored, dispatch, read] = useSignalReducer(
        reduceWithTransitions,
        () => initialQuizReviewState(activityId),
        (transitions: PhaseTransition[]) => {
            for (const { from, to, event } of transitions) telemetry.transition(from, to, event)
        }
    )

    useEffect(() => {
        dispatch({ type: "activity", activityId })
    }, [activityId, dispatch])
    const state = stored.activityId === activityId ? stored : initialQuizReviewState(activityId)

    // La feuille ouverte sans sa correction : on la relit, une fois par passage à `loading`.
    const loading = stored.open != null && stored.status === "loading" ? stored.activityId : null
    useEffect(() => {
        if (loading == null) return
        const id = loading
        let cancelled = false
        void (async () => {
            try {
                const quiz = await getQuiz(id)
                if (cancelled || read().activityId !== id) return
                dispatch({ type: "loaded", quiz })
            } catch (error) {
                if (cancelled || read().activityId !== id) return
                telemetry.error("load_review", error)
                dispatch({ type: "loadFailed" })
            }
        })()
        return () => {
            cancelled = true
        }
        // Une relecture par passage à `loading` : le service et la télémétrie ne changent pas en route.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, stored.status])

    const attempted = useCallback((quiz: ApiQuizForReview | null) => dispatch({ type: "attempted", quiz }), [dispatch])
    const open = useCallback(() => dispatch({ type: "requested" }), [dispatch])
    const retry = useCallback(() => dispatch({ type: "retry" }), [dispatch])
    const close = useCallback(() => dispatch({ type: "closed" }), [dispatch])

    return useMemo(
        () => ({ state, blocking: quizReviewBlocks(state), attempted, open, retry, close }),
        [state, attempted, open, retry, close]
    )
}
